import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { mapInformationBar } from "../information-bar/information-bar.utils";
import { MainHeaderEvents } from "./main-header.events";
import {
  sliceIntoChunks,
  CategoriesResponseItem,
  useAnalytics,
  Category,
  makeUrl,
  Product,
  ITheme,
  useAppLocation,
  useAppSearchParams,
  useAppNavigation,
  useAppNavigate,
  StoreMetadata,
  DBProduct,
  useStoreImages,
  getEnvVar,
  Addon,
  getAddonFromStoreAddons,
  AddonTypeId,
} from "@commons-monorepo/index";
import {
  HEADER_CATEGORIES_EXTEND_ITEMS,
  HEADER_CATEGORIES_SHOW_PARAM,
} from "./main-header.constants";

export const useMainHeader = ({
  data,
  goToCheckout: goToCheckoutFn,
  theme: themeContext,
  isPortraitInView,
}: {
  data: {
    avatarProfile: string;
    cartProducts: DBProduct[];
    categories: CategoriesResponseItem[];
    isMobile: boolean;
    storeMetadata: StoreMetadata;
    user?: any;
    userAddons: Addon<any>[];
  };
  isPortraitInView: boolean;
  theme: ITheme;
  goToCheckout: () => void;
}) => {
  const { t } = useTranslation();
  const navigate = useAppNavigate();
  const [logEvent] = useAnalytics({ storeMetadata: data.storeMetadata });
  const navigation = useAppNavigation();
  const [searchParams, setSearchParams] = useAppSearchParams();
  const { pathname } = useAppLocation();
  const { partnerProfilePicture } = useStoreImages(data?.storeMetadata);

  const storeMetadata = data?.storeMetadata;
  const isMobile = data?.isMobile;
  const partnerNetwork = storeMetadata?.userNetwork;
  const partnerId = storeMetadata?.userNetwork?.urlSummer;
  const customerId = "";
  const cartProducts = data?.cartProducts;
  const categories = data?.categories;
  const isLoggedIn = data?.user !== null;
  const ownerId = storeMetadata?.configuration?.ownerId;
  const storeUserPathName = storeMetadata?.userNetwork.urlSummer || ownerId;
  const categoriesListRef = useRef<HTMLDivElement>(null);
  const [showSearchBar, handleShowSearchBarState] = useState(false);
  const [showCartEmpty, setShowCartEmpty] = useState(false);
  const [showCategoriesMenu, setShowCategoriesMenu] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [cartEmptyTimeout, setCartEmptyTimeout] = useState<NodeJS.Timeout>();
  const [valueToSearch, setValueToSearch] = useState("");
  const [suggestedProducts, setSuggestedProducts] = useState<Product[]>();
  const [categoryListSelected, setCategoryListSelected] =
    useState<CategoriesResponseItem>();
  const [creatingShoppingCart, setCreatingShoppingCart] = useState(false);

  const informationBarData = mapInformationBar(storeMetadata, searchParams);
  const { informationBar, informationBarOffer } = informationBarData;
  const isPopUpDetailOpen = pathname.includes("contactanos");
  const checkLogo = `${
    getEnvVar("PUBLIC_ENV") === "local" ? getEnvVar("PUBLIC_HOST") : ""
  }/prod/web/catalogue/verification-check-2.webp`;

  const SHOW_LOGIN_BUTTON = false;

  const showStoreVerification = useMemo(() => {
    const verifiedAddon = getAddonFromStoreAddons<{
      status: "completed" | "rejected";
    }>(data.userAddons, AddonTypeId.StoreVerification);
    return verifiedAddon?.body?.status === "completed";
  }, [data.userAddons]);

  const categoriesChunks = useMemo(
    () =>
      !!categories?.length
        ? sliceIntoChunks(
            [...categories].sort((c) => (c.subCategories?.length > 0 ? -1 : 1)),
            categories?.length <= HEADER_CATEGORIES_EXTEND_ITEMS
              ? HEADER_CATEGORIES_EXTEND_ITEMS
              : categories?.length <= 20
              ? 5
              : 10,
          )
        : [],
    [categories],
  );

  const cartProductsQuantity = useMemo(
    () => cartProducts?.reduce((p, c) => p + c.quantity, 0),
    [cartProducts],
  );

  const handleShowLogin = (): void => {
    logEvent(MainHeaderEvents.SelectProfile);
    const to = `${pathname}/${makeUrl(pathname, "iniciar-sesion")}`;
    navigate(to, {
      relative: "path",
    });
  };

  const goToCheckout = useCallback(() => {
    goToCheckoutFn();
  }, [goToCheckoutFn, storeMetadata]);

  const setShowSearchBar = (state: boolean) => {
    handleShowSearchBarState(state);
  };

  const handleCartButtonClick = () => {
    if (!!cartProducts.length) {
      setCreatingShoppingCart(true);
      goToCheckout();
    }
  };

  const handleOnSearch = (query: string) => {
    if (query) {
      const to = `${pathname}/${makeUrl(pathname, `buscar/${query}`)}`;
      navigate(to, {
        relative: "path",
      });
    } else navigate(`/`, { relative: "path" });
  };

  const handleCloseSession = (): void => {
    localStorage.clear();
    location.reload();
  };

  const handleShowStoreInfo = () => {
    const to = `${pathname}/${makeUrl(pathname, "contactanos")}`;
    navigate(to, {
      relative: "path",
    });
    logEvent(MainHeaderEvents.SelectStoreInfo, { source: "header" });
  };

  const handleAboutUs = () => {
    navigate(`${pathname}/${makeUrl(pathname, `sobre-nosotros`)}`);
  };

  const removeCatQueryParam = useCallback(() => {
    searchParams.delete(HEADER_CATEGORIES_SHOW_PARAM);
    setSearchParams(searchParams);
    navigate(-1);
  }, [pathname, searchParams]);

  const handleCartButton = () => {
    if (!!cartProductsQuantity) {
      handleCartButtonClick();
    } else {
      setShowCartEmpty(true);
      if (cartEmptyTimeout) {
        clearTimeout(cartEmptyTimeout);
      }
      setCartEmptyTimeout(
        setTimeout(() => {
          setShowCartEmpty(false);
        }, 5000),
      );
    }
  };

  const handleCloseCategoriesList = (event?: Event) => {
    if (!event || !categoriesListRef.current?.contains(event.target as Node)) {
      const showCategories = searchParams.get(HEADER_CATEGORIES_SHOW_PARAM);
      if (showCategories !== null) {
        removeCatQueryParam();
      }
      setShowCategoriesMenu(false);
      setCategoryListSelected(undefined);
      document.removeEventListener(
        "mousedown",
        handleCloseCategoriesList,
        false,
      );
    }
  };

  const handleShowCategoriesList = () => {
    setShowCategoriesMenu(true);
    logEvent(MainHeaderEvents.SelectHeaderCategories, {
      Template_id: themeContext.templateId,
    });
    document.addEventListener("mousedown", handleCloseCategoriesList, false);
  };

  const handleClickOnCategory = useCallback(
    (category: Category) => {
      setShowCategoriesMenu(false);
      logEvent(MainHeaderEvents.SelectHeaderCategory, {
        Template_id: themeContext.templateId,
        category_name: category.name,
      });
    },
    [pathname],
  );

  const handleSelectCategoryList = useCallback(
    (category: CategoriesResponseItem) => {
      setCategoryListSelected(
        categoryListSelected !== category ? category : undefined,
      );
    },
    [categoryListSelected],
  );

  useEffect(() => {
    const handlePageShow = () => {
      setCreatingShoppingCart(false);
    };
    window.addEventListener("pageshow", handlePageShow);
    return () => {
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, []);

  useEffect(() => {
    if (navigation?.state === "loading") {
      setValueToSearch("");
      setShowSearchBar(false);
    }
  }, [navigation]);

  useEffect(() => {
    const showCategories = searchParams.get(HEADER_CATEGORIES_SHOW_PARAM);
    if (showCategories !== null) {
      handleShowCategoriesList();
    }
  }, [searchParams]);

  return {
    t,
    SHOW_LOGIN_BUTTON,
    logEvent,
    themeContext,
    handleShowLogin,
    handleCartButtonClick,
    handleOnSearch,
    isLoggedIn,
    showSearchBar,
    isMobile,
    storeMetadata,
    partnerId,
    partnerNetwork,
    cartProductsQuantity,
    isPortraitInView,
    isPopUpDetailOpen,
    showCategoriesMenu,
    showCartEmpty,
    showProfileMenu,
    creatingShoppingCart,
    setCreatingShoppingCart,
    setShowProfileMenu,
    setShowCartEmpty,
    handleCloseSession,
    handleShowStoreInfo,
    handleCartButton,
    setShowSearchBar,
    suggestedProducts,
    setSuggestedProducts,
    customerId,
    handleAboutUs,
    valueToSearch,
    setValueToSearch,
    partnerProfilePicture,
    informationBar,
    informationBarOffer,
    categoriesChunks,
    categoriesListRef,
    categoryListSelected,
    handleShowCategoriesList,
    handleClickOnCategory,
    handleSelectCategoryList,
    storeUserPathName,
    checkLogo,
    showStoreVerification,
  };
};
